import React, { useEffect, useState } from 'react'
import { ScrollView } from 'react-native'

import { FlashList } from '@shopify/flash-list'
import AddedFromAgentWebsiteFilter from '@zet/agent-website/src/components/addedFromAgentWebsiteFilter'
import { publishEvent } from '@zet/analytics'
import { useDebounce } from '@zet/common/hooks/useDebounce'
import { MY_TEAMS_ADVISOR } from '@zet/constants'
import { InitialFilter } from '@zet/constants/nonVerifiedTabs'
import { colors as zetColors } from '@zet/design-system'
import { Icon } from '@zet/design-system/icons'
import { Stack, XStack } from '@zet/design-system/layout'
import Pressable from '@zet/design-system/pressable'
import { B2 } from '@zet/design-system/typography'
import { APP_NAME } from '@zet/end-points'
import { useUserContext } from '@zet/feature-user'
import { i18n } from '@zet/localization'

import { useCustomerCount, useListOfCustomer } from '../../api/hooks'
import CustomerCard from '../../components/CustomerCard'
import CustomerListSkeleton from '../../layout/customerListSkeleton'
import useMyCustomersStore from '../../store/myCustomers'
import EmptyCustomers from './EmptyCustomers'

export default function NonVerifiedCustomers({ status, navigation }) {
  const [selectedStatus, setSelectedStatus] = useState<Array<number>>(
    InitialFilter[0].value
  )
  const [selectedSource, setSelectedSource] = useState<Array<string>>(
    InitialFilter[0].customerSource
  )
  const [filter, setFilter] = useState<string>('All')
  const [isRefresh, setIsRefresh] = useState<boolean>(false)
  const searchedText = useMyCustomersStore((state) => state.searchText)
  const { data: subAgent } = useUserContext()
  const nonVerifiedSortType = useMyCustomersStore(
    (state) => state.nonVerifiedSortType
  )
  const setSortingOptions = useMyCustomersStore(
    (state) => state.setSortingOptions
  )
  const { refetch: customerCountRefetch } = useCustomerCount(
    subAgent?.id?.toString() || ''
  )
  const debouncedSearchText = useDebounce(searchedText?.trim() as never, 600)

  const {
    data,
    isLoading,
    fetchNextPage,
    hasNextPage,
    refetch,
    isFetching,
    isFetchingNextPage,
  } = useListOfCustomer({
    searchText: debouncedSearchText,
    selectedStatus,
    sortType: nonVerifiedSortType,
    subAgentId: subAgent?.id ?? '',
    type: 'notVerified',
    selectedSource: selectedSource,
  })

  const onRefresh = () => {
    setIsRefresh(true)
    customerCountRefetch()
    refetch().finally(() => setIsRefresh(false))
  }

  const customersList =
    data?.pages
      ?.map((page) => page?.customerList)
      .flat()
      .filter((item) => Boolean(item)) || []

  const renderItem = ({ item }) => {
    let errorMessage
    InitialFilter.map((value) => {
      if (value.value.includes(+item.status)) {
        errorMessage = i18n.t(value.label)
      }
    })

    return (
      <CustomerCard
        name={item.fullName}
        phone={item.phoneNumber}
        errorMessage={errorMessage}
        isVerified={false}
        customerAdditionSource={item.customerAdditionSource}
        subAgentId={item?.subagentId}
      />
    )
  }

  useEffect(() => {
    if (status) {
      setFilter(InitialFilter[status].name)
      setSelectedStatus(InitialFilter[status].value)
      setSelectedSource(InitialFilter[status].customerSource)
    }
  }, [])

  useEffect(() => {
    setSortingOptions('nonverified')
    const unsubscribe = navigation.addListener('focus', () => {
      setSortingOptions('nonverified')
    })
    return unsubscribe
  }, [navigation])

  useEffect(() => {
    const unsubscribe = navigation.addListener('tabPress', (e) => {
      publishEvent('click_action', {
        item_name: 'add_customer_tab_navbar',
        cta_name: 'non-verified',
      })
    })

    return unsubscribe
  }, [navigation])

  if (isLoading) {
    return (
      <>
        {Array(7)
          .fill({})
          .map((_, index) => (
            <React.Fragment key={index}>
              <CustomerListSkeleton />
            </React.Fragment>
          ))}
      </>
    )
  }

  return (
    <Stack className="bg-grays-30 flex-1">
      <Stack className="bg-neutral-white100 ">
        <ScrollView
          horizontal={true}
          className="flex-2 bg-neutral-white100 z-10 mr-3 px-3 py-2"
          showsHorizontalScrollIndicator={false}
        >
          {InitialFilter.map((value, index) => {
            return (
              <Pressable
                key={`${value.name}-${index}`}
                onPress={() => {
                  setFilter(value.name)
                  setSelectedStatus(value.value)
                  setSelectedSource(value.customerSource)
                }}
                className={`mr-3 flex-row items-center rounded-md ${
                  filter === value.name
                    ? 'bg-primary-150'
                    : 'border-grays-40 border'
                } px-2 py-[6]`}
                eventName="click_action"
                eventPayload={{
                  item_name: 'add_customer_non_verified_filter',
                  filter_name: value.name,
                }}
              >
                <XStack>
                  <Icon
                    type={value.icon}
                    color={`${
                      filter === value.name
                        ? zetColors.grays[30]
                        : zetColors.grays[80]
                    }`}
                  />

                  <B2
                    className={`${value.icon ? 'ml-2' : 'mx-2'} ${
                      filter === value.name ? 'text-grays-30' : 'text-grays-80'
                    }`}
                  >
                    {i18n.t(value.label)}
                  </B2>
                </XStack>
              </Pressable>
            )
          })}
          {APP_NAME !== MY_TEAMS_ADVISOR && (
            <AddedFromAgentWebsiteFilter
              filter={filter}
              setFilter={setFilter}
              setSelectedStatus={setSelectedStatus}
              setSelectedSource={setSelectedSource}
            />
          )}
        </ScrollView>
      </Stack>
      <FlashList
        data={customersList}
        renderItem={renderItem}
        keyExtractor={(item) => String(item?.id)}
        estimatedItemSize={70}
        ListFooterComponent={
          isFetchingNextPage ? (
            <>
              {Array(2)
                .fill({})
                .map((_, index) => (
                  <Stack key={index} className="bg-white py-2">
                    <CustomerListSkeleton />
                  </Stack>
                ))}
            </>
          ) : (
            <Stack className="h-1" />
          )
        }
        onEndReached={() => {
          if (!isFetching && hasNextPage) {
            fetchNextPage()
          }
        }}
        ListEmptyComponent={<EmptyCustomers />}
        onRefresh={onRefresh}
        refreshing={isRefresh}
        keyboardDismissMode="on-drag"
        keyboardShouldPersistTaps="always"
      />
    </Stack>
  )
}
